import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  PaperAirplaneIcon, 
  PaperClipIcon,
  MagnifyingGlassIcon as SearchIcon
} from '@heroicons/react/24/outline';
import { getAIResponse } from '../services/ai';
import { useStore } from '../services/chatStore';

interface ChatWindowProps {
  chatId: string;
}

export default function ChatWindow({ chatId }: ChatWindowProps) {
  const { chats, addMessage } = useStore();
  const currentChat = chats[chatId];
  const messages = currentChat?.messages || [];
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Reset input when changing chats
  useEffect(() => {
    setNewMessage('');
    setIsLoading(false);
  }, [chatId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      const userMessage = newMessage;
      setNewMessage('');
      setIsLoading(true);

      try {
        // Add user message first
        addMessage(chatId, userMessage, true);

        // Get AI response with full chat history
        const aiResponse = await getAIResponse(
          userMessage,
          currentChat?.messages || []
        );
        
        // Add AI response
        addMessage(
          chatId, 
          typeof aiResponse === 'string' ? aiResponse : JSON.stringify(aiResponse),
          false
        );
      } catch (error) {
        addMessage(chatId, "Sorry, I couldn't process that request.", false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    const messageContainer = document.querySelector('.message-container');
    if (messageContainer) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  }, [messages]);

  // Function to scroll to message
  const scrollToMessage = useCallback((messageId: number) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight effect
      messageElement.classList.add('bg-yellow-100', 'dark:bg-yellow-900');
      setTimeout(() => {
        messageElement.classList.remove('bg-yellow-100', 'dark:bg-yellow-900');
      }, 2000);
    }
  }, []);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query) {
      const foundMessage = messages.find(msg => 
        msg.text.toLowerCase().includes(query.toLowerCase())
      );
      if (foundMessage) {
        scrollToMessage(foundMessage.id);
      }
    }
  }, [messages, scrollToMessage]);

  return (
    <div className="flex-1 flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Bar */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {currentChat?.name}
            </h2>
            <div className="ml-2 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900">
              <span className="text-xs font-medium text-green-800 dark:text-green-100">
                {messages.length} messages
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search messages..."
                className="pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messageContainerRef}
        className="flex-1 overflow-hidden hover:overflow-y-auto custom-scrollbar"
      >
        <div className="p-4 space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id}
              id={`message-${message.id}`}
              className={`flex gap-3 ${message.sent ? 'justify-end' : 'justify-start'} transition-colors duration-300`}
            >
              {/* Avatar for AI */}
              {!message.sent && (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm">
                  AI
                </div>
              )}
              
              <div className={`group relative max-w-[70%] ${
                message.sent ? 'order-1' : 'order-2'
              }`}>
                <div className={`px-4 py-2 rounded-2xl ${
                  message.sent 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white dark:bg-gray-800 shadow-md dark:text-white'
                }`}>
                  <p className="whitespace-pre-wrap break-words">{message.text}</p>
                  <div className={`text-xs mt-1 ${message.sent ? 'text-blue-100' : 'text-gray-400'}`}>
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>

              {/* Avatar for User */}
              {message.sent && (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm">
                  You
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm">
                AI
              </div>
              <div className="bg-white shadow-md rounded-2xl rounded-bl-none px-4 py-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
            <button 
              type="button" 
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <PaperClipIcon className="h-5 w-5 text-gray-500" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-transparent focus:outline-none min-w-0 text-gray-700"
              disabled={isLoading}
            />
            <button 
              type="submit"
              className={`p-2 rounded-full transition-colors ${
                isLoading 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              disabled={isLoading}
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 